import { ChangeEvent, useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIContent, DCUITheme } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { SelectOption } from "@idw-types/common";
import { DCUIThemingThemeValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { RadioInputFormGroup } from "../../../../../common/radio-input-form-group/radio-input-form-group";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { ColorPickerFormGroup } from "../../../../../common/color-picker-form-group/color-picker-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingThemeHeaderTextSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    dcuiContent: DCUIContent;
    dcuiTheme: DCUITheme;
    inheritedDCUIContent: DCUIContent;
    inheritedDCUITheme: DCUITheme;
    pageLevel: PageLevelTypes;
    validationErrors: DCUIThemingThemeValidationErrors;
    updateDCUIContentPropertyValue: (value: DCUIContent) => void;
    updateDCUIThemePropertyValue: (value: DCUITheme) => void;
};

export const DCUIThemingThemeHeaderTextSection = (props: DCUIThemingThemeHeaderTextSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        dcuiContent,
        dcuiTheme,
        inheritedDCUIContent,
        inheritedDCUITheme,
        pageLevel,
        validationErrors,
        updateDCUIThemePropertyValue,
        updateDCUIContentPropertyValue
    } = props;


    const handleAddHeaderTextChange = (newValue: string) => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            header: {
                ...dcuiTheme.header,
                showText: isStringTrue(newValue)
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleResetAddHeaderText = () => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            header: {
                ...dcuiTheme.header,
                showText: inheritedDCUITheme.header.showText
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleHeaderTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentPropertyValue({
            ...dcuiContent,
            header: {
                ...dcuiContent.header,
                text: event.target.value
            }
        });
    };

    const handleResetHeaderText = () => {
        updateDCUIContentPropertyValue({
            ...dcuiContent,
            header: {
                ...dcuiContent.header,
                text: inheritedDCUIContent.header.text
            }
        });
    };

    const handleHeaderTextPositionChange = (newValue: SelectOption) => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            header: {
                ...dcuiTheme.header,
                alignText: newValue.label
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleResetHeaderTextPosition = () => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            header: {
                ...dcuiTheme.header,
                alignText: inheritedDCUITheme.header.alignText
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleHeaderTextColorChange = (newValue: string) => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            header: {
                ...dcuiTheme.header,
                textColor: newValue
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleResetHeaderTextColor = () => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            header: {
                ...dcuiTheme.header,
                textColor: inheritedDCUITheme.header.textColor
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const headerTextPositionOptions = useMemo(() => {
        return [
            { label: "center", value: "center" },
            { label: "left", value: "left" },
            { label: "right", value: "right" },
        ];
    }, []);

    const selectedHeaderTextPositionOption = useMemo(() => {
        if (dcuiTheme.header.alignText) {
            return headerTextPositionOptions.find(po => po.label === dcuiTheme.header.alignText);
        }
        return null;
    }, [dcuiTheme.header.alignText]);

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUITheme.header.showText}
                currentThemingPropertyValue={dcuiTheme.header.showText}
                pageLevel={pageLevel}
            >
                <RadioInputFormGroup
                    controlId={`dcui-theming-header-add-header-text-radio-input`}
                    automationId={`dcui-theming-header-add-header-text-radio-input`}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerText.inputs.addHeaderText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAddHeaderTextChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    value={dcuiTheme.header.showText ? "true" : "false"}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerText.inputs.addHeaderText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetAddHeaderText}
                />

            </ThemingFormGroupWrapper>
            {dcuiTheme.header.showText && (
                <>
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedDCUIContent.header.text}
                        currentThemingPropertyValue={dcuiContent.header.text}
                        pageLevel={pageLevel}
                    >
                        <InputFormGroup
                            controlId={"dcui-theming-header-header-text-input"}
                            automationId={"dcui-theming-header-header-text-input"}
                            label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerText.inputs.headerText.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerText.inputs.headerText.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleHeaderTextChange}
                            value={dcuiContent.header.text || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerText.inputs.headerText.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetHeaderText}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.headerTextError}
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedDCUITheme.header.alignText}
                        currentThemingPropertyValue={dcuiTheme.header.alignText}
                        pageLevel={pageLevel}
                    >
                        <SelectFormGroup
                            controlId={"dcui-theming-header-header-text-postition-select-input"}
                            automationId={"dcui-theming-header-header-text-position-select-input"}
                            label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerText.inputs.headerTextPosition.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerText.inputs.headerTextPosition.label}
                            disabled={isDisabled}
                            isClearable={false}
                            isRequired={true}
                            isSearchable={false}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleHeaderTextPositionChange}
                            options={headerTextPositionOptions}
                            selectedOption={selectedHeaderTextPositionOption}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerText.inputs.headerTextPosition.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetHeaderTextPosition}
                        />
                    </ThemingFormGroupWrapper>

                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedDCUITheme.header.textColor}
                        currentThemingPropertyValue={dcuiTheme.header.textColor}
                        pageLevel={pageLevel}
                    >
                        <ColorPickerFormGroup
                            controlId={"dcui-theming-header-header-text-color-picker"}
                            automationId={"dcui-theming-header-header-text-color-picker"}
                            label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerText.inputs.headerTextColor.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerText.inputs.headerTextColor.label}
                            disabled={isDisabled}
                            isRequired={true}
                            layout={layout}
                            labelWidth={labelWidth}
                            value={dcuiTheme.header.textColor || ""}
                            onChange={handleHeaderTextColorChange}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerText.inputs.headerTextColor.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetHeaderTextColor}
                        />
                    </ThemingFormGroupWrapper>
                </>
            )}
        </div>
    );
};